import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface FuturisticSelectOption {
  value: string;
  label: string;
  description?: string;
}

interface FuturisticSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FuturisticSelectOption[];
  placeholder?: string;
  className?: string;
}

const FuturisticSelect: React.FC<FuturisticSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select',
  className = ''
}) => {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const selected = options.find(o => o.value === value) || null;

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="futuristic-select-trigger"
        style={{
          width: '100%',
          backgroundColor: 'var(--bg-subtle)',
          border: `1px solid var(--border-default)`,
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          textAlign: 'left',
          color: 'var(--fg-primary)',
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-sm)',
          transition: `all var(--motion-duration-fast) var(--motion-ease-standard)`,
          paddingRight: 'var(--space-10)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          outline: 'none'
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? selected.label : <span style={{ color: 'var(--fg-disabled)' }}>{placeholder}</span>}
        <ChevronDown 
          className="absolute"
          style={{
            right: 'var(--space-4)',
            top: '50%',
            transform: `translateY(-50%) ${open ? 'rotate(180deg)' : 'rotate(0deg)'}`,
            width: 'var(--space-5)',
            height: 'var(--space-5)',
            color: 'var(--accent)',
            transition: `transform var(--motion-duration-fast) var(--motion-ease-standard)`
          }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute custom-scrollbar"
          style={{
            zIndex: 'var(--z-dropdown)',
            marginTop: 'var(--space-2)',
            width: '100%',
            maxHeight: '16rem',
            overflowY: 'auto',
            borderRadius: 'var(--radius-lg)',
            border: `1px solid var(--border-default)`,
            backgroundColor: 'var(--bg-elevated)',
            boxShadow: 'var(--shadow-lg)',
            backdropFilter: 'blur(12px)'
          }}
        >
          {options.length === 0 && (
            <div 
              style={{
                padding: `var(--space-2) var(--space-4)`,
                fontSize: 'var(--font-size-xs)',
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--fg-disabled)'
              }}
            >
              No options
            </div>
          )}
          {options.map(opt => (
            <div
              role="option"
              key={opt.value}
              aria-selected={value === opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`futuristic-select-option ${value === opt.value ? 'selected' : ''}`}
              style={{
                padding: `var(--space-2) var(--space-4)`,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                transition: `all var(--motion-duration-fast) var(--motion-ease-standard)`,
                backgroundColor: value === opt.value ? 'var(--accent-bg-subtle)' : 'transparent',
                color: value === opt.value ? 'var(--accent)' : 'var(--fg-primary)'
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FuturisticSelect;


